// Scoring the player's forecasts against what actually happened.
//
// Pure functions over the debrief payload and the elicitation record. Everything the calibration panel
// draws comes from here, and `tests/ui/elicit.test.ts` drives it directly.
//
// Ground truth comes from the two places the debrief already holds it: `red.decision` events, which
// carry the red cell's own play id and reason string, and the truth frames, which are a sample of every
// unit's real position once a sim minute. That is the whole reason this exercise is possible — in a
// real command post nobody ever finds out what the enemy main effort *was*.

import type { Event, Unit, UnitType } from '../sim/types';
import { TICKS_PER_MIN } from '../sim/types';
import type { TruthFrame } from '../sim/sim';
import type { ElicitAnswer } from '../state/elicit';
import { corridorFrame, thirdNameOf, type CorridorFrame } from '../ai/copContext';
import { brierScore, reliabilityBins, type ForecastPoint, type ReliabilityBin } from '../ai/calibrationMath';
import { RESERVE_WINDOW_MIN } from '../ai/estimator';

const RED_MANEUVER: ReadonlySet<UnitType> = new Set<UnitType>(['armor', 'mech', 'inf']);
const RESERVE_COMMIT_PLAYS: ReadonlySet<string> = new Set(['red:reserve', 'red:forward']);

/** One forecast, resolved. */
export interface ResolvedAnswer {
  answer: ElicitAnswer;
  /** What actually happened, as a string: 'yes'/'no' for the reserve, a third name for main effort. */
  truth: string;
  /** Whether the thing the player put their probability on is what happened. */
  correct: boolean;
  /** The player's probability on the outcome that occurred (so a 'no' answer of 30% scores as 70%). */
  playerP: number;
  /** The estimator's probability on the same thing. */
  estimatorP: number;
  /** One line for the table: what was asked, what was said, what happened. */
  line: string;
}

/** Ticks at which red actually committed its reserve (or, under 'forward', its whole force). */
export function reserveCommitTicks(events: readonly Event[]): number[] {
  const out: number[] = [];
  for (const ev of events) {
    if (ev.kind !== 'red.decision') continue;
    const playId = ev.payload.playId;
    if (typeof playId === 'string' && RESERVE_COMMIT_PLAYS.has(playId)) out.push(ev.t);
  }
  return out.sort((a, b) => a - b);
}

/** The truth frame at or just before a tick. */
function frameAt(truth: readonly TruthFrame[], tick: number): TruthFrame | null {
  let best: TruthFrame | null = null;
  for (const f of truth) {
    if (f.tick <= tick && (!best || f.tick > best.tick)) best = f;
  }
  return best;
}

/**
 * Where red's maneuver combat power actually was at a tick, as a third name.
 *
 * Strength-weighted, and read from the truth frame rather than the final snapshot, so a forecast made
 * at H+20 is scored against H+20 and not against how the fight ended. The same definition the
 * calibration harness labels with (`trueMainEffortThird`, src/ai/jevDataset.ts) — they have to agree
 * or the player and the estimator are being marked against different exams.
 */
export function trueMainEffortAt(
  truth: readonly TruthFrame[], units: readonly Unit[], frame: CorridorFrame, tick: number,
): string | null {
  const snap = frameAt(truth, tick);
  if (!snap) return null;
  const typeById = new Map(units.map((u) => [u.id, u]));
  const sums: [number, number, number] = [0, 0, 0];
  for (const u of snap.units) {
    const def = typeById.get(u.id);
    if (!def || def.side !== 1 || !u.alive || !RED_MANEUVER.has(def.type)) continue;
    const i = frame.names.indexOf(thirdNameOf(frame, u));
    if (i >= 0) sums[i] += Math.max(0, u.strength) / 100;
  }
  if (sums[0] + sums[1] + sums[2] === 0) return null;
  let best = 1;
  for (const i of [1, 0, 2]) if (sums[i] > sums[best]) best = i;
  return frame.names[best];
}

export interface ResolveArgs {
  answers: readonly ElicitAnswer[];
  events: readonly Event[];
  truth: readonly TruthFrame[];
  units: readonly Unit[];
  graphics: readonly { id: string; kind: string; name: string; pts: { x: number; y: number }[]; radiusM?: number }[];
  objectiveId?: string;
}

/** Resolves every forecast that can be resolved. Answers whose truth cannot be established (no truth
 * frame, no red maneuver units left) are dropped rather than guessed — a Brier score computed over
 * invented outcomes is worse than no score. */
export function resolveAnswers(args: ResolveArgs): ResolvedAnswer[] {
  const frame = corridorFrame(
    args.graphics as Parameters<typeof corridorFrame>[0],
    args.objectiveId,
  );
  const commits = reserveCommitTicks(args.events);
  const out: ResolvedAnswer[] = [];

  for (const answer of args.answers) {
    if (answer.question === 'reserveCommitSoon') {
      const windowEnd = answer.t + RESERVE_WINDOW_MIN * TICKS_PER_MIN;
      const happened = commits.some((t) => t > answer.t && t <= windowEnd);
      const truth = happened ? 'yes' : 'no';
      // The player's chips are always the probability of "yes".
      const playerP = happened ? answer.playerProb : 1 - answer.playerProb;
      const estYes = answer.estimatorEstimate.probability;
      out.push({
        answer,
        truth,
        correct: happened === (answer.playerProb >= 0.5),
        playerP,
        estimatorP: happened ? estYes : 1 - estYes,
        line: `H+${Math.round(answer.t / TICKS_PER_MIN)}: you said ${Math.round(answer.playerProb * 100)}% `
          + `the reserve would commit within ${RESERVE_WINDOW_MIN} minutes — it ${happened ? 'did' : 'did not'}.`,
      });
      continue;
    }

    const truth = trueMainEffortAt(args.truth, args.units, frame, answer.t);
    if (truth === null || !answer.playerChoice) continue;
    const correct = truth === answer.playerChoice;
    const estP = answer.estimatorEstimate.value === truth
      ? answer.estimatorEstimate.probability
      : 1 - answer.estimatorEstimate.probability;
    out.push({
      answer,
      truth,
      correct,
      // The probability the player put on the option that turned out right. A 70% on the wrong third is
      // not a 30% on the right one when there are three options, but it is the closest honest reading
      // that keeps the score on the same axis as the yes/no question, and the panel says so.
      playerP: correct ? answer.playerProb : 1 - answer.playerProb,
      estimatorP: estP,
      line: `H+${Math.round(answer.t / TICKS_PER_MIN)}: you said ${Math.round(answer.playerProb * 100)}% `
        + `${answer.playerChoice} — it was ${truth}.`,
    });
  }

  return out;
}

export interface Scoreboard {
  n: number;
  /** Lower is better. 0.25 is what you get by saying 50% to everything. */
  playerBrier: number;
  estimatorBrier: number;
  /** Which provider answered the questions in this run, for the label. */
  provider: 'heuristic' | 'jev' | 'mixed' | null;
  calibrated: boolean;
  calibratedOnRuns: number;
  playerBins: ReliabilityBin[];
  estimatorBins: ReliabilityBin[];
}

export function scoreboard(resolved: readonly ResolvedAnswer[], bins = 5): Scoreboard {
  const playerPoints: ForecastPoint[] = resolved.map((r) => ({ p: r.playerP, outcome: 1 }));
  const estimatorPoints: ForecastPoint[] = resolved.map((r) => ({ p: r.estimatorP, outcome: 1 }));
  const providers = new Set(resolved.map((r) => r.answer.estimatorEstimate.provider));

  return {
    n: resolved.length,
    playerBrier: brierScore(playerPoints),
    estimatorBrier: brierScore(estimatorPoints),
    provider: providers.size === 0 ? null : providers.size > 1 ? 'mixed' : [...providers][0],
    calibrated: resolved.every((r) => r.answer.estimatorEstimate.calibrated) && resolved.length > 0,
    calibratedOnRuns: resolved[0]?.answer.estimatorEstimate.calibratedOnRuns ?? 0,
    playerBins: reliabilityBins(playerPoints, bins),
    estimatorBins: reliabilityBins(estimatorPoints, bins),
  };
}

/**
 * The one line of plain language.
 *
 * It looks for the single most repeated probability the player used and reports how often the thing
 * they said happened actually happened — "you said 70% three times, it happened once" — because that
 * is a statement about their own numbers they can check, not a judgment about their judgment. Falls
 * back to the overall comparison when no probability was used more than once.
 */
export function plainLanguageRead(resolved: readonly ResolvedAnswer[]): string {
  if (resolved.length === 0) return 'No forecasts were made in this run.';

  const reserve = resolved.filter((r) => r.answer.question === 'reserveCommitSoon');
  const byProb = new Map<number, { total: number; happened: number }>();
  for (const r of reserve) {
    const key = Math.round(r.answer.playerProb * 100);
    const row = byProb.get(key) ?? { total: 0, happened: 0 };
    row.total += 1;
    if (r.truth === 'yes') row.happened += 1;
    byProb.set(key, row);
  }

  let worst: { prob: number; total: number; happened: number; gap: number } | null = null;
  for (const [prob, row] of byProb) {
    if (row.total < 2) continue;
    const gap = Math.abs(prob / 100 - row.happened / row.total);
    if (!worst || gap > worst.gap) worst = { prob, total: row.total, happened: row.happened, gap };
  }

  if (worst && worst.gap >= 0.2) {
    const direction = worst.prob / 100 > worst.happened / worst.total ? 'overconfident' : 'underconfident';
    const times = worst.total === 2 ? 'twice' : `${worst.total} times`;
    const outcome = worst.happened === 0 ? 'it never happened'
      : worst.happened === 1 ? 'it happened once'
        : `it happened ${worst.happened} times`;
    return `You were ${direction} on reserve commitment: said ${worst.prob}% ${times}, ${outcome}.`;
  }

  const s = scoreboard(resolved);
  if (s.playerBrier < s.estimatorBrier - 0.02) {
    return `You beat the S-2 estimate over ${s.n} forecasts (Brier ${s.playerBrier.toFixed(2)} against ${s.estimatorBrier.toFixed(2)}; lower is better).`;
  }
  if (s.estimatorBrier < s.playerBrier - 0.02) {
    return `The S-2 estimate beat you over ${s.n} forecasts (Brier ${s.estimatorBrier.toFixed(2)} against ${s.playerBrier.toFixed(2)}; lower is better).`;
  }
  return `You and the S-2 estimate were within a hair of each other over ${s.n} forecasts (Brier ${s.playerBrier.toFixed(2)} against ${s.estimatorBrier.toFixed(2)}).`;
}
